/**
 * Seeding control panel with progress visualization
 */

import { useMemo } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Sparkles,
  Globe,
  CheckCircle,
  Clock,
  Zap,
  AlertTriangle,
  Timer,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSeeding, type CountryInfo } from '@/hooks/useSeeding';
import { countries } from '@/data/countries';
import { cn } from '@/lib/utils';

// Prepare country info for seeding
const countryInfoList: CountryInfo[] = countries.map((c) => ({
  code: c.code,
  name: c.name,
  capital: c.details?.capital ?? '',
}));

interface StatusCardProps {
  label: string;
  value: number | string;
  subtext?: string;
  icon: React.ElementType;
  variant?: 'default' | 'success' | 'warning';
}

function StatusCard({ label, value, subtext, icon: Icon, variant = 'default' }: StatusCardProps) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'p-2 rounded-lg',
            variant === 'success' && 'bg-green-100 text-green-700',
            variant === 'warning' && 'bg-amber-100 text-amber-700',
            variant === 'default' && 'bg-muted text-muted-foreground'
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
          <p className="text-xs text-muted-foreground">
            {label}
            {subtext && <span className="ml-1">{subtext}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

export function SeedingPanel() {
  const {
    status,
    isSeeding,
    loading,
    error,
    lastResult,
    estimatedHoursRemaining,
    countdown,
    isRateLimited,
    start,
    pause,
    reset,
  } = useSeeding(countryInfoList);

  const progressPercentage = useMemo(() => {
    if (!status) return 0;
    return Math.round((status.completedCountries / status.totalCountries) * 100);
  }, [status]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Bulk Photo Seeding
        </CardTitle>
        <CardDescription>
          Automatically fetch photos from Unsplash for all countries.
          Rate limited to 50 requests per hour (2 photos per country = 25 countries/hour).
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatusCard
            label="Total Countries"
            value={status?.totalCountries ?? countries.length}
            icon={Globe}
          />
          <StatusCard
            label="Completed"
            value={status?.completedCountries ?? 0}
            icon={CheckCircle}
            variant="success"
          />
          <StatusCard
            label="Remaining"
            value={status?.remainingCountries ?? countries.length}
            icon={Clock}
          />
          <StatusCard
            label="API Requests"
            value={status?.rateLimitRemaining ?? 50}
            subtext="/50 this hour"
            icon={Zap}
            variant={
              (status?.rateLimitRemaining ?? 50) < 10 ? 'warning' : 'default'
            }
          />
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Seeding Progress</span>
            <span className="font-medium tabular-nums">
              {status?.completedCountries ?? 0} / {status?.totalCountries ?? countries.length}
              ({progressPercentage}%)
            </span>
          </div>
          <Progress value={progressPercentage} className="h-3" />
          {status && status.remainingCountries > 0 && (
            <p className="text-xs text-muted-foreground">
              {isRateLimited ? (
                <span className="text-amber-600 font-medium">Waiting for rate limit reset...</span>
              ) : (
                <>
                  Estimated time remaining: ~{estimatedHoursRemaining} hour(s)
                  {isSeeding && lastResult?.processedCountries?.[0] && (
                    <span className="ml-2">
                      | Last processed: <strong>{lastResult.processedCountries.slice(-1)[0]}</strong>
                    </span>
                  )}
                </>
              )}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {!isSeeding ? (
            <Button
              onClick={start}
              disabled={loading}
            >
              <Play className="h-4 w-4 mr-2" />
              {status && status.completedCountries > 0 ? 'Resume Seeding' : 'Start Seeding'}
            </Button>
          ) : (
            <Button onClick={pause} variant={isRateLimited ? 'outline' : 'secondary'}>
              <Pause className="h-4 w-4 mr-2" />
              {isRateLimited ? 'Cancel Wait' : 'Pause'}
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={loading || isSeeding}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Progress
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset Seeding Progress?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear all seeding progress tracking. Existing photos will NOT be deleted,
                  but they may be re-fetched when seeding runs again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => reset()}>Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Rate Limit Warning with Countdown */}
        {isRateLimited && countdown && (
          <Alert className="border-amber-200 bg-amber-50">
            <Timer className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Rate Limit Reached - Auto-resuming</AlertTitle>
            <AlertDescription className="text-amber-700">
              <div className="flex items-center gap-2">
                <span>Resuming in</span>
                <span className="font-mono font-bold text-lg tabular-nums">
                  {countdown.minutes}:{countdown.seconds.toString().padStart(2, '0')}
                </span>
              </div>
              <p className="mt-1 text-sm">
                Unsplash API rate limit (50/hour) reached. Waiting for reset.
                You can leave this page open or pause to resume later.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Rate limit warning when not actively seeding */}
        {!isRateLimited && status && status.rateLimitRemaining < 2 && !isSeeding && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>Rate Limit Low</AlertTitle>
            <AlertDescription>
              Unsplash API rate limit is nearly exhausted. Starting seeding will trigger
              an automatic wait once the limit is hit.
            </AlertDescription>
          </Alert>
        )}

        {/* Last Result Summary */}
        {lastResult && (
          <div className="rounded-lg border p-4 bg-muted/50">
            <h4 className="font-medium mb-2">Last Batch Result</h4>
            <p className="text-sm text-muted-foreground">
              Processed {lastResult.processed} countries
              {lastResult.errors.length > 0 && (
                <span className="text-destructive">
                  {' '}
                  with {lastResult.errors.length} error(s)
                </span>
              )}
            </p>
            {lastResult.processedCountries.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Countries: {lastResult.processedCountries.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Error List */}
        {status?.errors && status.errors.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Errors During Seeding ({status.errors.length})</AlertTitle>
            <AlertDescription>
              <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {status.errors.slice(0, 10).map((err) => (
                  <li key={err.country_code} className="text-xs">
                    <strong>{err.country_code}:</strong> {err.error}
                  </li>
                ))}
                {status.errors.length > 10 && (
                  <li className="text-xs">...and {status.errors.length - 10} more</li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
